import { KeyboardEvent, FC } from 'react';
import { ApolloConsumer } from '@apollo/client';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { InputAutoSuggestWithLabel } from '@blueprint/text-input';
import { useDispatch, useSelector } from 'react-redux';
import { GEO_LOCATIONS_QUERY } from '../../../queries/search/geo-locations';
import ADDRESS_POSTCODE_SUGGESTION_QUERY from 'queries/search/addressPostcode.graphql';
import { updateFilter, locationTextSelector } from 'reducers/filters';
import { Tab } from 'homepage/components/HeroContainer/HeroContainer.types';
import {
    transformGeoLocationData,
    transformAddressPostcodeSuggestion
} from './transforms';

type LocationSearchInputProps = {
    id?: string;
    onLocationInputKeyDown: () => void;
    onInputTextChange?: (value: string) => void;
    hasExtendedHeight?: boolean;
    hasBorderRight?: boolean;
    hasDarkPurpleBorder?: boolean;
    onlyDisplayRemoveIconWhenActive?: boolean;
    searchType?: Tab;
    value: string;
    showError?: boolean;
};

type LocationType = {
    id: string;
    value: string | number | string[];
};

const forSaleToRentPlaceholder = 'eg. Oxford or NW3';
const housePricesPlaceholder = 'Enter street or postcode';

export const LocationSearchInput: FC<LocationSearchInputProps> = ({
    id,
    onLocationInputKeyDown,
    hasExtendedHeight,
    hasBorderRight,
    hasDarkPurpleBorder,
    onlyDisplayRemoveIconWhenActive,
    searchType,
    value,
    showError = false
}) => {
    const reduxDispatch = useDispatch();

    const { selectedValue: locationTextValue } =
        useSelector(locationTextSelector);

    const onLocationSelect = (location: LocationType) => {
        reduxDispatch(updateFilter('q', location.value as string));
        reduxDispatch(updateFilter('identifier', location.id));
        reduxDispatch(updateFilter('locationText', ''));
    };

    const handleInputTextChange = (value: string) => {
        reduxDispatch(updateFilter('locationText', value));
    };

    const handleInputKeyDown = (evt: KeyboardEvent) => {
        evt.preventDefault();

        if (evt.key === 'Enter') {
            onLocationInputKeyDown();
        }
    };
    const isHousePricesSearch = searchType === 'house-prices';

    return (
        <>
            <ApolloConsumer>
                {(client) => {
                    const locationRegex = /([A-Za-zÀ-ÖØ-öø-ÿ])/;
                    const housePricesRegex = /([0-9A-Za-zÀ-ÖØ-öø-ÿ])/;

                    const getGeoLocationData = async (value: string) => {
                        const { data } = await client.query({
                            query: GEO_LOCATIONS_QUERY,
                            variables: { locationPrefix: value }
                        });

                        return transformGeoLocationData(data.geoSuggestion);
                    };

                    const getAddressPostcodeSuggestion = async (
                        value: string
                    ) => {
                        const { data } = await client.query({
                            query: ADDRESS_POSTCODE_SUGGESTION_QUERY,
                            variables: { addressPartial: value.toLowerCase() }
                        });

                        const locationTextValueSplit =
                            locationTextValue.split(' ');
                        const firstElement = locationTextValueSplit[0];
                        const isFirstElementANumber = !isNaN(
                            parseInt(firstElement, 10)
                        );

                        const prefixElement = isFirstElementANumber
                            ? firstElement
                            : '';

                        return transformAddressPostcodeSuggestion(
                            data.getSuggestedAddresses,
                            prefixElement
                        );
                    };

                    const getSuggestionFunction = isHousePricesSearch
                        ? getAddressPostcodeSuggestion
                        : getGeoLocationData;

                    const getSuggestionFunctionDebounced =
                        AwesomeDebouncePromise(getSuggestionFunction, 300, {
                            onlyResolvesLast: true
                        });

                    return (
                        <InputAutoSuggestWithLabel
                            getData={getSuggestionFunctionDebounced}
                            suggestionSelectedAction={onLocationSelect}
                            placeholder={
                                isHousePricesSearch
                                    ? housePricesPlaceholder
                                    : forSaleToRentPlaceholder
                            }
                            validInputRegex={
                                isHousePricesSearch
                                    ? housePricesRegex
                                    : locationRegex
                            }
                            showError={showError}
                            id={id}
                            value={value}
                            autoComplete="off"
                            inputOnKeyDown={handleInputKeyDown}
                            inputTextChange={handleInputTextChange}
                            hasExtendedHeight={hasExtendedHeight}
                            hasDarkPurpleBorder={hasDarkPurpleBorder}
                            hasBorderRight={hasBorderRight}
                            onlyDisplayRemoveIconWhenActive={
                                onlyDisplayRemoveIconWhenActive
                            }
                        />
                    );
                }}
            </ApolloConsumer>
        </>
    );
};
